import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { refresh } = useAuth();
    const [state, setState] = useState({ status: "checking", data: null, error: null });
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", error: "Missing session_id" });
            return;
        }
        const poll = async () => {
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                // A subscription checkout is "complete" either when paid OR trialing (no immediate charge)
                if (data.status === "complete" || data.payment_status === "paid" || data.trialing) {
                    await refresh();
                    setState({ status: "paid", data });
                    return;
                }
                if (data.status === "expired") {
                    setState({ status: "expired", data });
                    return;
                }
                if (attempts.current >= 8) {
                    setState({ status: "timeout", data });
                    return;
                }
                attempts.current += 1;
                setTimeout(poll, 2000);
            } catch (err) {
                setState({ status: "error", error: err?.response?.data?.detail || "Status check failed" });
            }
        };
        poll();
    }, [sessionId, refresh]);

    return (
        <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
            <div className="max-w-lg w-full border border-white/10 p-10 bg-[#0a0a0a]" data-testid="billing-success-root">
                {state.status === "checking" && (
                    <>
                        <Loader2 className="w-10 h-10 animate-spin text-white" strokeWidth={1.5} />
                        <div className="mt-6 font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500">// VERIFYING PAYMENT</div>
                        <h1 className="mt-2 font-display font-black text-3xl tracking-tighter">Hang tight…</h1>
                        <p className="mt-3 text-zinc-400 text-sm">We&apos;re confirming your payment with Stripe. This usually takes 2–5 seconds.</p>
                    </>
                )}
                {state.status === "paid" && (
                    <>
                        <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={1.5} />
                        <div className="mt-6 font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500">
                            // {state.data?.trialing ? "FREE TRIAL ACTIVE" : "PAYMENT CONFIRMED"}
                        </div>
                        <h1 className="mt-2 font-display font-black text-3xl tracking-tighter">
                            You&apos;re on <span className="capitalize">{state.data.plan}</span>.
                        </h1>
                        <p className="mt-3 text-zinc-400 text-sm">
                            {state.data?.trialing
                                ? "Your 7-day free trial has started. You won't be charged until it ends — cancel anytime from the billing portal."
                                : "Welcome aboard. Your monthly credits have been refreshed."}
                        </p>
                        <Link to="/app/new">
                            <Button data-testid="billing-success-cta" className="mt-8 w-full bg-white text-black hover:bg-zinc-200 h-11 font-semibold">
                                Start repurposing <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </>
                )}
                {(state.status === "expired" || state.status === "timeout") && (
                    <>
                        <XCircle className="w-10 h-10 text-white" strokeWidth={1.5} />
                        <div className="mt-6 font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500">// {state.status}</div>
                        <h1 className="mt-2 font-display font-black text-3xl tracking-tighter">Payment not completed</h1>
                        <p className="mt-3 text-zinc-400 text-sm">If you were charged, contact support. Otherwise, try again.</p>
                        <Link to="/pricing">
                            <Button variant="outline" className="mt-8 w-full bg-transparent border-white/15 hover:bg-white/5 text-white h-11">
                                Back to pricing
                            </Button>
                        </Link>
                    </>
                )}
                {state.status === "error" && (
                    <>
                        <XCircle className="w-10 h-10 text-white" strokeWidth={1.5} />
                        <h1 className="mt-6 font-display font-black text-3xl tracking-tighter">Something went wrong</h1>
                        <p className="mt-3 text-zinc-400 text-sm">{state.error}</p>
                        <Link to="/pricing">
                            <Button variant="outline" className="mt-8 w-full bg-transparent border-white/15 hover:bg-white/5 text-white h-11">
                                Back to pricing
                            </Button>
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
